import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getSafeReturnUrl } from '../utils/safeReturnUrl';
import PilotCodeOfConductModal from '../components/PilotCodeOfConductModal';

const AdminLogin = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingPilotSession, setPendingPilotSession] = useState(null);
  const { login, completeLoginAfterPilotConduct, isAuthenticated, loading: authLoading, instructor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrlRaw = searchParams.get('returnUrl');
  const role = (instructor?.accessRole || '').toLowerCase();

  useEffect(() => {
    if (!authLoading && isAuthenticated && !pendingPilotSession) {
      if (role === 'owner') {
        navigate('/owner/dashboard', { replace: true });
        return;
      }
      if (role === 'partner') {
        navigate('/partner/dashboard', { replace: true });
        return;
      }
      const next = getSafeReturnUrl(returnUrlRaw);
      navigate(next || '/admin', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, returnUrlRaw, pendingPilotSession, role]);

  const finishLogin = (loggedInInstructor = null) => {
    const loggedInRole = (loggedInInstructor?.accessRole || role || '').toLowerCase();
    if (loggedInRole === 'owner') {
      navigate('/owner/dashboard', { replace: true });
      return;
    }
    if (loggedInRole === 'partner') {
      navigate('/partner/dashboard', { replace: true });
      return;
    }
    const next = getSafeReturnUrl(returnUrlRaw);
    navigate(next || '/admin', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      if (result.needsPilotConductAck) {
        setPendingPilotSession({ token: result.token, instructor: result.instructor });
      } else {
        finishLogin(result.instructor);
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handlePilotConductConfirm = () => {
    if (!pendingPilotSession) return;
    completeLoginAfterPilotConduct(pendingPilotSession.token, pendingPilotSession.instructor);
    setPendingPilotSession(null);
    finishLogin(pendingPilotSession.instructor);
  };

  return (
    <div className="container">
      <div className="card admin-auth-card">
        <h2>{t('adminLogin.title')}</h2>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('adminLogin.emailOrUsername')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading || Boolean(pendingPilotSession)}
              placeholder={t('adminLogin.placeholder')}
            />
          </div>
          <div className="form-group">
            <label>{t('adminLogin.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading || Boolean(pendingPilotSession)}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading || Boolean(pendingPilotSession)}
          >
            {loading ? t('adminLogin.loggingIn') : t('adminLogin.login')}
          </button>
        </form>
        <p className="admin-auth-forgot">
          <Link to="/admin/forgot-password">{t('adminLogin.forgotLink')}</Link>
        </p>
      </div>
      {pendingPilotSession && (
        <PilotCodeOfConductModal onConfirm={handlePilotConductConfirm} />
      )}
    </div>
  );
};

export default AdminLogin;
